import { AgentMode } from 'features/chatbot/types/chat'
import { ConversationMessage } from 'features/chatbot/types/conversation'
import { deserializeConversation } from 'features/chatbot/utils/deserializeConversation'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { generateChatSessionId, SerializedConversationMessage } from 'types'

const MAX_SESSION_NAME_LENGTH = 40

export interface ChatSessionStreamState {
  activeStreamId: string | null
  activeMessageId: string | null
  lastSeq: number
  lastMessageStatus: 'in_progress' | 'complete' | 'error' | null
}

export interface ChatSession {
  id: string
  entryId: string | null
  sessionId: string
  name: string | undefined
  conversation: ConversationMessage[]
  isFromHistory: boolean
  stream: ChatSessionStreamState
  /**
   * Mode pinned at session-create time by an external caller (e.g.
   * the case page's "Ask about this case" button). When set, the
   * first send uses this mode instead of the default 'auto' and the
   * backend skips routing.
   */
  pinnedMode?: AgentMode
  /**
   * Extra context fields to merge into the backend session's
   * `context` payload at create time (e.g. `{ case_id }`). Used both
   * for routing-time awareness on `auto` and for tool defaults on
   * pinned modes.
   */
  pinnedContext?: Record<string, string>
}

export interface CreateSessionOptions {
  mode?: AgentMode
  context?: Record<string, string>
}

export interface SessionSnapshot {
  name?: string | undefined
  conversation: ConversationMessage[]
  activeStreamId?: string | null
  activeMessageId?: string | null
  lastSeq?: number
  lastMessageStatus?: ChatSessionStreamState['lastMessageStatus']
}

interface ChatbotSessionContextProps {
  activeSession: ChatSession
  openSessions: ChatSession[]
  createSession: (opts?: CreateSessionOptions) => void
  loadSession: (
    entryId: string,
    sessionId: string,
    chatLog: SerializedConversationMessage[],
    name: string | undefined
  ) => void
  hydrateSession: (entryId: string, snapshot: SessionSnapshot) => void
  switchSession: (id: string) => void
  closeSession: (id: string) => void
  renameSession: (id: string, name: string) => void
  updateConversation: (
    sessionId: string,
    update:
      | ConversationMessage[]
      | ((prev: ConversationMessage[]) => ConversationMessage[])
  ) => void
  updateStreamState: (
    sessionId: string,
    update: Partial<ChatSessionStreamState>
  ) => void
  attachEntryId: (sessionId: string, entryId: string) => void
  deriveSessionName: (sessionId: string, firstMessage: string) => void
}

const ChatbotSessionContext = createContext<ChatbotSessionContextProps>(null!)

function emptyStreamState(): ChatSessionStreamState {
  return {
    activeStreamId: null,
    activeMessageId: null,
    lastSeq: 0,
    lastMessageStatus: null,
  }
}

function createNewSession(
  useV2: boolean,
  opts?: CreateSessionOptions
): ChatSession {
  const { entryId, sessionId } = generateChatSessionId()
  return {
    id: crypto.randomUUID(),
    entryId: useV2 ? null : entryId,
    sessionId,
    name: undefined,
    conversation: [],
    isFromHistory: false,
    stream: emptyStreamState(),
    pinnedMode: opts?.mode,
    pinnedContext: opts?.context,
  }
}

const ChatbotApiVersionContext = createContext<boolean>(false)

export function useChatbotV2(): boolean {
  return useContext(ChatbotApiVersionContext)
}

export const ChatbotSessionProvider: React.FC<{
  children: React.ReactNode
  useV2?: boolean
}> = ({ children, useV2 = false }) => {
  const [sessions, setSessions] = useState<ChatSession[]>(() => [
    createNewSession(useV2),
  ])
  const [activeId, setActiveId] = useState<string>(() => sessions[0].id)
  const activeIdRef = useRef(activeId)
  activeIdRef.current = activeId

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) ?? sessions[0],
    [sessions, activeId]
  )

  const createSession = useCallback(
    (opts?: CreateSessionOptions) => {
      setSessions((prev) => {
        const currentActive = prev.find((s) => s.id === activeIdRef.current)
        // If the active session is an empty, unsent default chat (no
        // conversation, no backend entryId yet, not loaded from
        // history), reuse it by re-pinning its mode/context. Avoids
        // leaving a phantom "new chat" tab behind whenever the user
        // clicks "Ask about this case" before sending anything.
        const isEmptyDefault =
          currentActive !== undefined &&
          currentActive.entryId === null &&
          currentActive.conversation.length === 0 &&
          !currentActive.isFromHistory
        if (isEmptyDefault && currentActive) {
          return prev.map((s) =>
            s.id === currentActive.id
              ? {
                  ...s,
                  pinnedMode: opts?.mode,
                  pinnedContext: opts?.context,
                }
              : s
          )
        }
        const newSession = createNewSession(useV2, opts)
        setActiveId(newSession.id)
        return [newSession, ...prev]
      })
    },
    [useV2]
  )

  const loadSession = useCallback(
    (
      entryId: string,
      sessionId: string,
      chatLog: SerializedConversationMessage[],
      name: string | undefined
    ) => {
      setSessions((prev) => {
        const existing = prev.find((s) => s.entryId === entryId)
        if (existing) {
          setActiveId(existing.id)
          return prev
        }
        const loaded: ChatSession = {
          id: crypto.randomUUID(),
          entryId,
          sessionId,
          name,
          conversation: deserializeConversation(chatLog),
          isFromHistory: true,
          stream: emptyStreamState(),
        }
        setActiveId(loaded.id)
        return [...prev, loaded]
      })
    },
    []
  )

  const hydrateSession = useCallback(
    (entryId: string, snapshot: SessionSnapshot) => {
      setSessions((prev) => {
        const existing = prev.find((s) => s.entryId === entryId)
        if (existing) {
          setActiveId(existing.id)
          return prev.map((s) =>
            s.id === existing.id
              ? {
                  ...s,
                  name: snapshot.name ?? s.name,
                  conversation: snapshot.conversation,
                  stream: {
                    activeStreamId:
                      'activeStreamId' in snapshot
                        ? (snapshot.activeStreamId ?? null)
                        : s.stream.activeStreamId,
                    activeMessageId:
                      'activeMessageId' in snapshot
                        ? (snapshot.activeMessageId ?? null)
                        : s.stream.activeMessageId,
                    lastSeq:
                      'lastSeq' in snapshot
                        ? (snapshot.lastSeq ?? 0)
                        : s.stream.lastSeq,
                    lastMessageStatus:
                      'lastMessageStatus' in snapshot
                        ? (snapshot.lastMessageStatus ?? null)
                        : s.stream.lastMessageStatus,
                  },
                  isFromHistory: true,
                }
              : s
          )
        }
        const { sessionId: generatedSessionId } = generateChatSessionId()
        const loaded: ChatSession = {
          id: crypto.randomUUID(),
          entryId,
          sessionId: generatedSessionId,
          name: snapshot.name,
          conversation: snapshot.conversation,
          isFromHistory: true,
          stream: {
            activeStreamId: snapshot.activeStreamId ?? null,
            activeMessageId: snapshot.activeMessageId ?? null,
            lastSeq: snapshot.lastSeq ?? 0,
            lastMessageStatus: snapshot.lastMessageStatus ?? null,
          },
        }
        setActiveId(loaded.id)
        return [...prev, loaded]
      })
    },
    []
  )

  const switchSession = useCallback((id: string) => {
    setActiveId(id)
  }, [])

  const closeSession = useCallback((id: string) => {
    setSessions((prev) => {
      if (prev.length <= 1) return prev
      const idx = prev.findIndex((s) => s.id === id)
      const next = prev.filter((s) => s.id !== id)
      if (activeIdRef.current === id) {
        const newActiveIdx = Math.min(idx, next.length - 1)
        setActiveId(next[newActiveIdx].id)
      }
      return next
    })
  }, [])

  const renameSession = useCallback((id: string, name: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)))
  }, [])

  const updateConversation = useCallback(
    (
      targetSessionId: string,
      update:
        | ConversationMessage[]
        | ((prev: ConversationMessage[]) => ConversationMessage[])
    ) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== targetSessionId) return s
          const nextConversation =
            typeof update === 'function' ? update(s.conversation) : update
          return { ...s, conversation: nextConversation }
        })
      )
    },
    []
  )

  const updateStreamState = useCallback(
    (targetSessionId: string, update: Partial<ChatSessionStreamState>) => {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === targetSessionId
            ? { ...s, stream: { ...s.stream, ...update } }
            : s
        )
      )
    },
    []
  )

  const attachEntryId = useCallback(
    (targetSessionId: string, entryId: string) => {
      setSessions((prev) =>
        prev.map((s) => (s.id === targetSessionId ? { ...s, entryId } : s))
      )
    },
    []
  )

  const deriveSessionName = useCallback(
    (targetSessionId: string, firstMessage: string) => {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id !== targetSessionId || s.name !== undefined) return s
          const trimmed = firstMessage.trim()
          const derived =
            trimmed.length > MAX_SESSION_NAME_LENGTH
              ? `${trimmed.slice(0, MAX_SESSION_NAME_LENGTH)}...`
              : trimmed
          return { ...s, name: derived }
        })
      )
    },
    []
  )

  const value = useMemo<ChatbotSessionContextProps>(
    () => ({
      activeSession,
      openSessions: sessions,
      createSession,
      loadSession,
      hydrateSession,
      switchSession,
      closeSession,
      renameSession,
      updateConversation,
      updateStreamState,
      attachEntryId,
      deriveSessionName,
    }),
    [
      activeSession,
      sessions,
      createSession,
      loadSession,
      hydrateSession,
      switchSession,
      closeSession,
      renameSession,
      updateConversation,
      updateStreamState,
      attachEntryId,
      deriveSessionName,
    ]
  )

  return (
    <ChatbotApiVersionContext.Provider value={useV2}>
      <ChatbotSessionContext.Provider value={value}>
        {children}
      </ChatbotSessionContext.Provider>
    </ChatbotApiVersionContext.Provider>
  )
}

export function useChatbotSession(): ChatbotSessionContextProps {
  return useContext(ChatbotSessionContext)
}
